import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Button from '@/components/base/Button';
import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.blog';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${siteUrl}/cssbuy-spreadsheet#article`,
  headline: 'O que é CSSBuy Spreadsheet: Guia Completo para Compras Inteligentes',
  description: 'Guia completo sobre cssbuy spreadsheet: o que é, como funciona, categorias, dicas de segurança e comparação com alternativas.',
  image: 'https://readdy.ai/api/search-image?query=Modern%20organized%20home%20office%20with%20large%20monitor%20displaying%20colorful%20product%20catalog%20dashboard%2C%20fashion%20items%20like%20sneakers%20and%20accessories%20neatly%20arranged%20on%20desk%2C%20warm%20afternoon%20sunlight%20streaming%20through%20window%2C%20productive%20creative%20workspace%2C%20Brazilian%20home%20with%20tropical%20plant%20in%20corner%2C%20organized%20digital%20lifestyle%20photography%2C%20inspiring%20work%20environment%2C%20cream%20wood%20and%20green%20tones%2C%20cozy%20yet%20professional%20atmosphere&width=1200&height=600&seq=article-v2-hero&orientation=landscape',
  author: {
    '@type': 'Organization',
    name: 'Equipe CSSBuy Spreadsheet Brasil',
    url: `${siteUrl}/#organization`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'cssbuyspreadsheet.blog',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
    },
  },
  datePublished: '2026-06-27',
  dateModified: '2026-06-27',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/cssbuy-spreadsheet`,
  },
  inLanguage: 'pt-BR',
  isAccessibleForFree: true,
};

const articleBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Início',
      item: `${siteUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'O que é CSSBuy Spreadsheet',
      item: `${siteUrl}/cssbuy-spreadsheet`,
    },
  ],
};

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-foreground-400">
              <li>
                <a href="/" className="hover:text-foreground-700 transition-colors duration-200">
                  <i className="ri-home-line"></i> Início
                </a>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-700 font-medium">O que é CSSBuy Spreadsheet</li>
            </ol>
          </nav>

          {/* Category Tag */}
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <i className="ri-guide-line text-xs"></i>
            Guia Completo
          </span>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
            O que é CSSBuy Spreadsheet: Guia Completo para Compras Inteligentes
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-8">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              Equipe CSSBuy Spreadsheet Brasil
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Atualizado em Junho 2026
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-timer-line"></i>
              8 min de leitura
            </span>
          </div>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20organized%20home%20office%20with%20large%20monitor%20displaying%20colorful%20product%20catalog%20dashboard%2C%20fashion%20items%20like%20sneakers%20and%20accessories%20neatly%20arranged%20on%20desk%2C%20warm%20afternoon%20sunlight%20streaming%20through%20window%2C%20productive%20creative%20workspace%2C%20Brazilian%20home%20with%20tropical%20plant%20in%20corner%2C%20organized%20digital%20lifestyle%20photography%2C%20inspiring%20work%20environment%2C%20cream%20wood%20and%20green%20tones%2C%20cozy%20yet%20professional%20atmosphere&width=1200&height=600&seq=article-v2-hero&orientation=landscape"
              alt="CSSBuy Spreadsheet - Guia completo para compras inteligentes"
              className="w-full object-cover object-top"
            />
          </div>

          {/* Table of Contents */}
          <div className="bg-background-100 rounded-2xl p-6 mb-10">
            <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-list-check text-accent-600"></i>
              Índice do Conteúdo
            </h2>
            <ul className="space-y-2">
              {[
                'O que é o CSSBuy Spreadsheet?',
                'Por que usar um spreadsheet para compras?',
                'Como funciona o CSSBuy Spreadsheet',
                'Principais categorias disponíveis',
                'Vantagens de usar o CSSBuy Spreadsheet',
                'Como acessar e começar a usar',
                'Dicas de segurança essenciais',
                'CSSBuy Spreadsheet vs outras alternativas',
                'Perguntas frequentes',
                'Conclusão',
              ].map((item, i) => (
                <li key={i}>
                  <a
                    href={`#section-${i + 1}`}
                    className="text-foreground-600 hover:text-foreground-950 text-sm flex items-center gap-2 transition-colors duration-200"
                  >
                    <span className="w-5 h-5 rounded-full bg-accent-100 text-accent-700 text-xs flex items-center justify-center font-semibold shrink-0">
                      {i + 1}
                    </span>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Article Content */}
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
            {/* Section 1 */}
            <section id="section-1">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                O que é o CSSBuy Spreadsheet?
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                O <strong>cssbuy spreadsheet</strong> é uma ferramenta essencial para compradores internacionais que utilizam agentes de compra chineses. Trata-se de uma planilha organizada e constantemente atualizada que reúne os melhores produtos disponíveis para compra através do CSSBuy, um dos agentes de compras mais populares entre consumidores brasileiros e de todo o mundo.
              </p>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Diferente de outras planilhas genéricas, o <strong>cssbuy spreadsheet</strong> que recomendamos é atualizado diariamente, conta com mais de <strong>3000 produtos verificados</strong> e está organizado em mais de <strong>40 categorias exclusivas</strong>. Isso significa que você não precisa perder horas procurando produtos em fóruns, redes sociais ou plataformas desorganizadas — tudo está reunido em um só lugar, com links diretos e verificados.
              </p>
              <p className="text-base md:text-lg leading-relaxed">
                O <strong>melhor cssbuy spreadsheet</strong> do Brasil é aquele que combina quantidade de produtos, qualidade dos links, organização das categorias e atualização constante — exatamente o que oferecemos aqui no <strong>cssbuyspreadsheet.blog</strong>.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Por que usar um spreadsheet para compras?
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Comprar produtos da China através de agentes como o CSSBuy pode ser uma experiência incrível — ou uma grande frustração. A diferença está na qualidade da informação que você tem antes de fazer sua compra. Um <strong>cssbuy spreadsheet</strong> bem organizado elimina as principais dificuldades que compradores enfrentam:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'Encontrar produtos de qualidade sem perder tempo navegando em sites chineses',
                  'Evitar produtos falsificados ou de baixa qualidade',
                  'Comparar preços entre diferentes vendedores',
                  'Acessar links verificados pela comunidade',
                  'Descobrir novas marcas e tendências',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-base">
                    <span className="w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg leading-relaxed">
                Com o <strong>cssbuy spreadsheet 2026</strong>, você tem acesso a uma curadoria de produtos que já foram testados e aprovados por outros compradores, economizando tempo e dinheiro.
              </p>
            </section>

            {/* Section 3 */}
            <section id="section-3">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Como funciona o CSSBuy Spreadsheet
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Utilizar o <strong>cssbuy spreadsheet</strong> é extremamente simples. O processo foi pensado para ser intuitivo mesmo para quem nunca comprou da China antes. Veja o passo a passo:
              </p>
              <div className="space-y-4 mb-4">
                {[
                  { step: '1', title: 'Escolha uma categoria', desc: 'Navegue pelas 40+ categorias disponíveis no spreadsheet. De Shoes a Perfumes, tudo está organizado para facilitar sua busca.' },
                  { step: '2', title: 'Abra o spreadsheet', desc: 'Clique no botão "cssbuy sheet" ou "buy it now" para acessar a planilha completa com todos os produtos.' },
                  { step: '3', title: 'Copie o link do produto', desc: 'Cada produto no spreadsheet tem um link direto para a plataforma de compra. Basta copiar o link desejado.' },
                  { step: '4', title: 'Compre através do agente', desc: 'Cole o link no CSSBuy ou no seu agente de preferência e finalize a compra normalmente.' },
                  { step: '5', title: 'Receba seu pedido', desc: 'Acompanhe o envio e receba seus produtos no conforto da sua casa.' },
                ].map((stepItem) => (
                  <div key={stepItem.step} className="flex gap-4 p-4 bg-background-100 rounded-xl">
                    <span className="w-9 h-9 rounded-full bg-foreground-950 text-background-50 flex items-center justify-center font-bold text-sm shrink-0">
                      {stepItem.step}
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-1">{stepItem.title}</h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">{stepItem.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4 */}
            <section id="section-4">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Principais categorias disponíveis
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                O <strong>cssbuy spreadsheet</strong> cobre praticamente todas as categorias de produtos que você possa imaginar. As categorias são organizadas de forma lógica para que você encontre rapidamente o que procura. Confira as principais:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  'Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets',
                  'Pants/Shorts', 'Headwear', 'Sets', 'Underwear',
                  'Jersey', 'Accessories', 'Bags', 'Belts',
                  'Perfumes', 'Other Stuff',
                ].map((cat) => (
                  <span key={cat} className="px-3 py-2 bg-background-100 rounded-lg text-sm text-foreground-600 text-center">
                    {cat}
                  </span>
                ))}
              </div>
              <p className="text-base md:text-lg leading-relaxed">
                Cada categoria do <strong>cssbuy spreadsheet brasil</strong> contém dezenas ou centenas de produtos. Isso significa que, independentemente do seu estilo ou necessidade, você encontrará opções de qualidade no spreadsheet.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Vantagens de usar o CSSBuy Spreadsheet
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Se você ainda está em dúvida se vale a pena usar um <strong>cssbuy spreadsheet</strong>, aqui estão as principais vantagens que fazem toda a diferença na sua experiência de compra:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { icon: 'ri-refresh-line', title: 'Atualização diária', desc: 'Produtos novos todos os dias, links verificados constantemente.' },
                  { icon: 'ri-shield-check-line', title: 'Segurança garantida', desc: 'Links verificados manualmente para evitar golpes e produtos falsos.' },
                  { icon: 'ri-money-dollar-circle-line', title: 'Economia real', desc: 'Compare preços e encontre as melhores ofertas em um só lugar.' },
                  { icon: 'ri-group-line', title: 'Comunidade ativa', desc: 'Feedbacks reais de outros compradores brasileiros.' },
                  { icon: 'ri-book-open-line', title: 'Guias completos', desc: 'Tutoriais passo a passo para iniciantes e avançados.' },
                  { icon: 'ri-global-line', title: 'Foco no Brasil', desc: 'Conteúdo otimizado para compradores brasileiros.' },
                ].map((adv, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 rounded-xl">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${adv.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 text-sm mb-1">{adv.title}</h3>
                      <p className="text-foreground-500 text-xs leading-relaxed">{adv.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 6 */}
            <section id="section-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Como acessar e começar a usar
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Acessar o <strong>cssbuy spreadsheet</strong> é gratuito e não requer cadastro. Basta visitar o site principal através dos botões de acesso rápido disponíveis em nossa página inicial. O processo é totalmente descomplicado:
              </p>
              <div className="bg-accent-50 rounded-2xl p-6 border border-accent-100 mb-4">
                <p className="text-foreground-700 text-base leading-relaxed mb-3">
                  <i className="ri-lightbulb-line text-accent-600 mr-1.5"></i>
                  <strong>Dica importante:</strong> Salve o link do <strong>cssbuy spreadsheet</strong> nos seus favoritos para acessar rapidamente sempre que precisar. Como a planilha é atualizada diariamente, vale a pena conferir com frequência as novidades.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Button
                  href="https://kaspreadsheet.com"
                  variant="primary"
                  size="lg"
                  className="gap-2"
                >
                  Acessar Agora
                  <i className="ri-arrow-right-line text-lg"></i>
                </Button>
                <Button
                  href="/guia-de-compras"
                  variant="outline"
                  size="lg"
                >
                  Ver guia de compras
                </Button>
              </div>
            </section>

            {/* Section 7 */}
            <section id="section-7">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Dicas de segurança essenciais
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                A segurança deve ser sempre sua prioridade ao fazer compras internacionais. Embora o <strong>cssbuy spreadsheet</strong> seja uma ferramenta que ajuda a reduzir riscos, é fundamental seguir algumas práticas recomendadas:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'Nunca compartilhe informações pessoais desnecessárias com vendedores',
                  'Verifique as avaliações do produto antes de finalizar a compra',
                  'Utilize métodos de pagamento seguros oferecidos pelo agente',
                  'Desconfie de ofertas com preços muito abaixo do mercado',
                  'Leia os guias de segurança disponíveis em nosso site antes de comprar',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-base">
                    <span className="w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-xs"></i>
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg leading-relaxed">
                Para um guia mais detalhado sobre segurança, confira nossa seção dedicada: <a href="/guia-de-compras#replicas-qualidade" className="text-foreground-950 font-semibold underline hover:text-foreground-700">Guia de Qualidade e Segurança</a>.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                CSSBuy Spreadsheet vs outras alternativas
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Existem outras formas de encontrar produtos para comprar da China, mas nenhuma é tão eficiente quanto o <strong>cssbuy spreadsheet</strong>. Veja a comparação:
              </p>
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-foreground-950 text-background-50">
                      <th className="text-left p-4 rounded-tl-xl text-sm font-semibold">Recurso</th>
                      <th className="text-center p-4 text-sm font-semibold">CSSBuy Spreadsheet</th>
                      <th className="text-center p-4 text-sm font-semibold">Reddit/Fóruns</th>
                      <th className="text-center p-4 rounded-tr-xl text-sm font-semibold">Google Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: 'Produtos disponíveis', ours: '3000+', others1: 'Variável', others2: '500+' },
                      { feature: 'Frequência de atualização', ours: 'Diária', others1: 'Irregular', others2: 'Semanal' },
                      { feature: 'Categorias organizadas', ours: '40+', others1: 'Não', others2: '10+' },
                      { feature: 'Links verificados', ours: 'Sim', others1: 'Não', others2: 'Parcial' },
                      { feature: 'Guias e tutoriais', ours: 'Sim', others1: 'Não', others2: 'Não' },
                      { feature: 'Foco no Brasil', ours: 'Sim', others1: 'Não', others2: 'Não' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                        <td className="p-4 text-sm font-medium text-foreground-800">{row.feature}</td>
                        <td className="p-4 text-center text-sm text-accent-700 font-semibold">{row.ours}</td>
                        <td className="p-4 text-center text-sm text-foreground-500">{row.others1}</td>
                        <td className="p-4 text-center text-sm text-foreground-500">{row.others2}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 9 - FAQ */}
            <section id="section-9">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Perguntas frequentes
              </h2>
              <div className="space-y-3">
                {[
                  { q: 'O CSSBuy Spreadsheet é realmente gratuito?', a: 'Sim! O cssbuy spreadsheet é 100% gratuito. Não cobramos nada pelo acesso e não há planos pagos. Nosso objetivo é ajudar a comunidade.' },
                  { q: 'Preciso criar conta para acessar?', a: 'Não. O acesso ao cssbuy spreadsheet é direto e não requer qualquer tipo de cadastro ou login.' },
                  { q: 'Os produtos são originais?', a: 'O spreadsheet inclui tanto produtos originais quanto réplicas de alta qualidade. Cada link é verificado e a comunidade compartilha feedbacks sobre a qualidade.' },
                  { q: 'Posso sugerir novos produtos?', a: 'Sim! A comunidade do cssbuy spreadsheet está sempre aberta a sugestões. Entre em contato através do nosso site principal.' },
                ].map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-xl overflow-hidden group">
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-200/50 transition-colors duration-200">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 shrink-0"></i>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Section 10 - Conclusion */}
            <section id="section-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Conclusão
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                O <strong>cssbuy spreadsheet</strong> é, sem dúvida, a ferramenta mais completa e confiável para quem deseja fazer compras inteligentes através de agentes chineses. Com mais de 3000 produtos organizados, atualizações diárias e uma comunidade ativa de compradores brasileiros, não há motivo para continuar perdendo tempo com informações dispersas em redes sociais e fóruns.
              </p>
              <p className="text-base md:text-lg leading-relaxed mb-4">
                Seja você um comprador iniciante ou experiente, o <strong>cssbuy spreadsheet 2026</strong> vai transformar sua experiência de compra. Comece agora mesmo a explorar as categorias e descubra produtos incríveis com preços que cabem no seu bolso.
              </p>
              <div className="bg-foreground-950 rounded-2xl p-6 md:p-8 text-center mt-8">
                <h3 className="text-background-50 font-bold text-xl mb-2">
                  Pronto para começar?
                </h3>
                <p className="text-background-50/60 text-sm mb-5">
                  Acesse agora o melhor cssbuy spreadsheet do Brasil.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    href="https://kaspreadsheet.com"
                    variant="primary"
                    size="lg"
                    className="bg-background-50 text-foreground-950 hover:bg-background-100 gap-2"
                  >
                    cssbuy sheet
                    <i className="ri-arrow-right-up-line text-lg"></i>
                  </Button>
                  <Button
                    href="https://kaspreadsheet.com"
                    variant="outline"
                    size="lg"
                    className="border-background-50/30 text-background-50 hover:border-background-50 hover:bg-background-50/10 gap-2"
                  >
                    buy it now
                    <i className="ri-external-link-line text-lg"></i>
                  </Button>
                </div>
              </div>
            </section>

            {/* Back to Home */}
            <div className="border-t border-background-200 pt-8 mt-12">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <a
                  href="/"
                  className="inline-flex items-center gap-2 text-foreground-600 hover:text-foreground-950 text-sm font-medium transition-colors duration-200"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar para a página inicial
                </a>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/guia-de-compras"
                    className="text-foreground-500 hover:text-foreground-800 text-xs transition-colors duration-200"
                  >
                    Guia de Compras →
                  </a>
                  <a
                    href="/guia-de-compras#replicas-qualidade"
                    className="text-foreground-500 hover:text-foreground-800 text-xs transition-colors duration-200"
                  >
                    Qualidade e Segurança →
                  </a>
                  <a
                    href="/guia-de-compras#guia-iniciantes"
                    className="text-foreground-500 hover:text-foreground-800 text-xs transition-colors duration-200"
                  >
                    Para Iniciantes →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleBreadcrumb),
        }}
      />

      <Footer />
    </main>
  );
}